import * as React from "react";
import { CheckIcon } from "../../../components/icons";
import { cn } from "../../../lib/utils";

export interface ProjectOptionProps {
  name: string;
  /** The model's reason for a suggestion, or the project's own description. */
  detail?: string | null;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  /**
   * Lines of detail before it is cut. A description is context and can be clipped; the
   * model's reason is the point of the suggestion card, so that one passes 0 for all of it.
   */
  detailLines?: number;
}

/**
 * One project the email could go to. The same row serves the suggestions and the full
 * list, so choosing the model's pick and choosing one yourself look like the same act --
 * which they are: nothing is filed until File is pressed.
 */
const ProjectOption = ({
  name,
  detail,
  selected,
  onSelect,
  disabled,
  detailLines = 2,
}: ProjectOptionProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    disabled={disabled}
    className={cn(
      "flex w-full items-start justify-start gap-2 rounded-[16px] border px-2.5 py-2 text-left outline-none focus-visible:outline-2 focus-visible:outline-primary disabled:pointer-events-none",
      selected ? "border-primary bg-primary-tint" : "border-border bg-transparent hover:bg-grey-50"
    )}
  >
    <div className="min-w-0 flex-1">
      <div className="truncate text-body2 font-semibold text-strong" title={name}>
        {name}
      </div>
      {detail && (
        <div
          className={cn(
            "text-caption text-muted-foreground",
            detailLines > 0 && "overflow-hidden [-webkit-box-orient:vertical] [display:-webkit-box]"
          )}
          // The count is a prop, so it cannot be a fixed line-clamp-N class.
          style={detailLines > 0 ? { WebkitLineClamp: detailLines } : undefined}
        >
          {detail}
        </div>
      )}
    </div>
    {/* The slot is always there so a row does not shift sideways when it is chosen. */}
    <span className="w-[18px] flex-none pt-0.5 text-primary">
      {selected && <CheckIcon className="size-[18px]" />}
    </span>
  </button>
);

export default ProjectOption;
